import contributorService from '@/Services/contributor';
import businessService from '@/Services/business';
import { getError } from '@/utils/helpers';
import { defineStore } from 'pinia';
import { ref } from 'vue';

export const useContributorStore = defineStore('contributor', () => {
  const business = ref(null),
   contributor = ref(null),
   contributors = ref([]),
   bestOfRated = ref(null),
   bestOfVoted = ref(null),
   popular = ref([]),
   topRated = ref([]),
   mostVotedBusinesses = ref([]),
   topRatedBusinesses = ref([]),
   similarBusinesses = ref([]),
   businesses = ref([]),
   loading = ref(false),
   loadingMostVotedBusinesses = ref(false),
   loadingTopRatedBusinesses = ref(false),
   loadingSimilarBusinesses = ref(false),
   loadingTopContributors = ref(false),
   error = ref(null),

  setContributor = async (value) => {
    contributor.value = value;
  },

  setContributors = async (value) => {
    contributors.value = value;
  },

  setPopular = async (value) => {
    popular.value = value;
  },

  setTopRated = async (value) => {
    topRated.value = value;
  },

  setBestOfVoted = async (value) => {
    bestOfVoted.value = value;
  },

  setBestOfRated = async (value) => {
    bestOfRated.value = value;
  },

  setBusiness = async (value) => {
    business.value = value;
  },

  setBusinesses = async (value) => {
    businesses.value = value;
  },

  setMostVotedBusinesses = async (value) => {
    mostVotedBusinesses.value = value;
  },

  setTopRatedBusinesses = async (value) => {
    topRatedBusinesses.value = value;
  },

  setSimilarBusinesses = async (value) => {
    similarBusinesses.value = value;
  },


  updateTopBusinessesLikeStatusForUser = async (businessId) => {
    const AllTopRatedBusinessesIndex = topRatedBusinesses.value.findIndex(business => business.id === businessId);     
    if (AllTopRatedBusinessesIndex !== -1) {
      topRatedBusinesses.value[AllTopRatedBusinessesIndex].liked = !topRatedBusinesses.value[AllTopRatedBusinessesIndex].liked;
      topRatedBusinesses.value[AllTopRatedBusinessesIndex].likeCount += topRatedBusinesses.value[AllTopRatedBusinessesIndex].liked ? 1 : -1;
    }
    topRatedBusinesses.value.sort((a, b) => b.likeCount - a.likeCount);
  },
  updateSimilarBusinessesLikeStatusForUser = async (businessId) => {
    const similarBusinessesIndex = similarBusinesses.value.findIndex(business => business.id === businessId);    
    if (similarBusinessesIndex !== -1) {
      similarBusinesses.value[similarBusinessesIndex].liked = !similarBusinesses.value[similarBusinessesIndex].liked;
      similarBusinesses.value[similarBusinessesIndex].likeCount += similarBusinesses.value[similarBusinessesIndex].liked ? 1 : -1;
    }
    similarBusinesses.value.sort((a, b) => b.likeCount - a.likeCount);
  },
  updateBusinessesLikeStatusForUser = async (businessId) => {
    const AllBusinessesIndex = businesses.value.findIndex(business => business.id === businessId);    
    if (AllBusinessesIndex !== -1) {
      businesses.value[AllBusinessesIndex].liked = !businesses.value[AllBusinessesIndex].liked;
      businesses.value[AllBusinessesIndex].likeCount += businesses.value[AllBusinessesIndex].liked ? 1 : -1;
    }
    businesses.value.sort((a, b) => b.likeCount - a.likeCount);

    const topRatedIndex = topRatedBusinesses.value.findIndex(business => business.id === businessId);
    if (topRatedIndex !== -1) {
      topRatedBusinesses.value[topRatedIndex].liked = !topRatedBusinesses.value[topRatedIndex].liked;
      topRatedBusinesses.value[topRatedIndex].likeCount += topRatedBusinesses.value[topRatedIndex].liked ? 1 : -1;
    }

    const mostVotedIndex = mostVotedBusinesses.value.findIndex(business => business.id === businessId);
    if (mostVotedIndex !== -1) {
      mostVotedBusinesses.value[mostVotedIndex].liked = !mostVotedBusinesses.value[mostVotedIndex].liked;
      mostVotedBusinesses.value[mostVotedIndex].likeCount += mostVotedBusinesses.value[mostVotedIndex].liked ? 1 : -1;
    }
    mostVotedBusinesses.value.sort((a, b) => b.likeCount - a.likeCount);
  },

  fetchContributors = async (sort_by = 'first_name') => {
    try {
      loading.value = true;
      const { data } = await contributorService.all(sort_by),
      contributors = data.data.map((business) =>
        contributorService.constructContributor(business)
      );
      await setContributors(contributors);
      await setPopular([]);
      await setTopRated([]);
      await setContributor(null);
      error.value = null;
    } catch (err) {
      error.value = getError(err);
    } finally {
      loading.value = false;
    }
  },

  fetchPopular = async (type = 'receivedVotes') => {
    try {
      loadingTopContributors.value = true;
      const { data } = await contributorService.popular(type),
      contributors = data.data.map((contributor) =>
        contributorService.constructContributor(contributor)
      );

      type === 'contributions'
        ? await setPopular({
          items: contributors, 
          likesCount: data.likesCount,
          postsCount: data.postsCount
        }) 
        : await setTopRated(contributors);
      error.value = null;
    } catch (err) {
      error.value = getError(err);
    } finally {
      loadingTopContributors.value = false;
    }
  },

  fetchContributor = async (id) => {
    try {
      loading.value = true;
      const { data } = await contributorService.contributor( id),
      contributor = contributorService.constructContributor(data);
      await setContributor(contributor);
      await setContributors([]);
      await setPopular([]);
      await setTopRated([]);
      error.value = null;
    } catch (err) {
      error.value = getError(err);
    } finally {
      loading.value = false;
    }
  },

  fetchBusinesses = async (contributor, sortBy = 'createdAt', orderBy = 'desc') => {
    try {
      loading.value = true;
      const { data } = await contributorService.businesses(contributor, sortBy, orderBy),
      businesses = data.data.map((business) =>
        businessService.constructBusiness(business)
      );
      await setBusinesses(businesses);
      await setMostVotedBusinesses([]);
      await setTopRatedBusinesses(null);
      error.value = null;
    } catch (err) {
      error.value = getError(err);
    } finally {
      loading.value = false;
    }
  },

  fetchMostVotedBusinesses = async (contributor, sector = null, category = null, limit = 5) => {
    try {
      loadingMostVotedBusinesses.value = true;
      let response;
      if (category || sector) {
        if (category) {
          response = await contributorService.mostVotedBusinessesByCategory(contributor, category, limit);
        } else {
          response = await contributorService.mostVotedBusinessesBySector(contributor, sector, limit);
        }
      } else {
        response = await contributorService.mostVotedBusinesses(contributor);
      }
      const { data } = response,
      businesses = data.data.map((business) =>
        businessService.constructBusiness(business)
      );
      await setMostVotedBusinesses(businesses);
      error.value = null;
    } catch (err) {
      error.value = getError(err);
    } finally {
      loadingMostVotedBusinesses.value = false;
    }
  },

  fetchTopRatedBusinesses = async (contributor, sector = null, category = null, limit = 5) => {
    try {
      loadingTopRatedBusinesses.value = true;
      let response;
      if (sector || category) {
        if (category) {
          response = await contributorService.topRatedBusinessesByCategory(contributor, category, limit);
        } else {
          response = await contributorService.topRatedBusinessesBySector(contributor, sector, limit);
        }
      } else {
        response = await contributorService.topRatedBusinesses(contributor);
      }
      const { data } = response,
      businesses = data.data.map((business) =>
        businessService.constructBusiness(business)
      );
      await setTopRatedBusinesses(businesses);
      error.value = null;
    } catch (err) {
      error.value = getError(err);
    } finally {
      loadingTopRatedBusinesses.value = false;
    }
  },
      fetchBestOfVotedBusinesses = async (contributor, sector = null, category = null) => {
        try {
          loadingMostVotedBusinesses.value = true;
          let response;
          if (category || sector) {
            if (category) {
              response = await contributorService.mostVotedBusinessesByCategory(contributor, category, 1);
            } else {
              response = await contributorService.mostVotedBusinessesBySector(contributor, sector, 1);
            }
          } else {
            response = await contributorService.mostVotedBusinesses(contributor, 1);
          }
          const { data } = response,
              business = data.data.length
                  ? businessService.constructBusiness(data.data[0])
                  : null;
          await setBestOfVoted(business);
          error.value = null;
        } catch (err) {
          error.value = getError(err);
        } finally {
          loadingMostVotedBusinesses.value = false;
        }
      },

      fetchBestOfRatedBusinesses = async (contributor, sector = null, category = null) => {
        try {
          loadingTopRatedBusinesses.value = true;
          let response;
          if (sector || category) {
            if (category) {
              response = await contributorService.topRatedBusinessesByCategory(contributor, category, 1);
            } else {
              response = await contributorService.topRatedBusinessesBySector(contributor, sector, 1);
            }
          } else {
            response = await contributorService.topRatedBusinesses(contributor, 1);
          }
          const { data } = response,
              businesses = data.data.map((business) =>
                  businessService.constructBusiness(business)
              );
          await setBestOfRated(businesses[0]);
          error.value = null;
        } catch (err) {
          error.value = getError(err);
        } finally {
          loadingTopRatedBusinesses.value = false;
        }
      },

  fetchBusinessesForSector = async (contributor, sector, sortBy = 'createdAt', orderBy = 'desc') => {
    try {
      loading.value = true;
      const { data } = await contributorService.businessesForSector(contributor, sector, sortBy, orderBy),
      businesses = data.data.map((business) =>
        businessService.constructBusiness(business)
      );
      await setBusinesses(businesses);
      await setBusiness(null);
      error.value = null;
    } catch (err) {
      error.value = getError(err);
    } finally {
      loading.value = false;
    }
  },

  fetchBusinessesForCategory = async (contributor, sector, category, sortBy = 'createdAt', orderBy = 'desc') => {
    try {
      loading.value = true;
      const { data } = await contributorService.businessesForCategory(contributor, sector, category, sortBy, orderBy),
      businesses = data.data.map((business) =>
        businessService.constructBusiness(business)
      );
      await setBusinesses(businesses);
      await setBusiness(null);
      error.value = null;
    } catch (err) {
      error.value = getError(err);
    } finally {
      loading.value = false;
    }
  },

  fetchBusiness = async (contributor, sector, category, id) => {
    try {
      loading.value = true;
      const { data } = await contributorService.business(contributor, sector, category, id),
      business = businessService.constructBusiness(data);
      await setBusiness(business);
      await setBusinesses([]);
      await setMostVotedBusinesses([]);
      await setTopRatedBusinesses([]);
      await setSimilarBusinesses([]);
      error.value = null;
    } catch (err) {
      error.value = getError(err);
    } finally {
      loading.value = false;
    }
  },

  fetchSimilarBusinesses = async (contributor, business) => {
    try {
      loadingSimilarBusinesses.value = true;
      const { data } = await contributorService.similarBusinesses(contributor, business),
      businesses = data.data.map((business) =>
        businessService.constructBusiness(business)
      );
      await setSimilarBusinesses(businesses);
      error.value = null;
    } catch (err) {
      error.value = getError(err);
    } finally {
      loadingSimilarBusinesses.value = false;
    }
  };

  return {
    contributor,
    contributors,
    popular,
    topRated,
    bestOfVoted,
    bestOfRated,
    businesses,
    mostVotedBusinesses,
    topRatedBusinesses,
    similarBusinesses,
    business,
    loading,
    loadingMostVotedBusinesses,
    loadingTopRatedBusinesses,
    loadingSimilarBusinesses,
    loadingTopContributors,
    error,
    setContributor,
    setContributors,
    setPopular,
    setTopRated,
    setBestOfVoted,
    setBestOfRated,
    setBusiness,
    setBusinesses,
    setMostVotedBusinesses,
    setTopRatedBusinesses,
    setSimilarBusinesses,
    updateBusinessesLikeStatusForUser,
    updateTopBusinessesLikeStatusForUser,
    updateSimilarBusinessesLikeStatusForUser,
    fetchContributors,
    fetchContributor,
    fetchPopular,
    fetchBusinesses,
    fetchBusinessesForSector,
    fetchBusinessesForCategory,
    fetchBusiness,
    fetchMostVotedBusinesses,
    fetchTopRatedBusinesses,
    fetchSimilarBusinesses,
    fetchBestOfVotedBusinesses,
    fetchBestOfRatedBusinesses
  };
});
